import React, { useEffect, useState } from 'react';
import { CheckCircle, Loader2, ArrowRight, Mail, MessageCircle } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/checkout/status/${sessionId}`);
        if (!response.ok) throw new Error('Failed to check status');

        const data = await response.json();
        setPaymentData(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling
        setTimeout(() => pollStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        setStatus('error');
      }
    };

    pollStatus();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#050506] flex items-center justify-center p-4">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-emerald-500/[0.03] rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-amber-500/[0.03] rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 w-full max-w-lg">
        {/* Glow */}
        <div className={`absolute -inset-2 rounded-3xl blur-2xl transition-colors duration-500 ${
          status === 'success' 
            ? 'bg-gradient-to-r from-emerald-500/30 via-teal-500/40 to-emerald-500/30' 
            : status === 'checking' 
              ? 'bg-gradient-to-r from-amber-500/30 via-orange-500/40 to-amber-500/30'
              : 'bg-gradient-to-r from-red-500/30 via-rose-500/40 to-red-500/30'
        }`} />

        {/* Card */}
        <div className="relative bg-gradient-to-b from-zinc-900 to-zinc-950 rounded-2xl p-8 sm:p-10 border border-zinc-800 text-center">
          
          {/* Checking status */}
          {status === 'checking' && (
            <>
              <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-amber-400 animate-spin" />
              </div>
              <h1 className="text-2xl font-black text-white mb-3">Проверяем оплату...</h1>
              <p className="text-zinc-400">Пожалуйста, подождите несколько секунд</p>
            </>
          )}

          {/* Success */}
          {status === 'success' && (
            <>
              <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-400" />
              </div>
              <h1 className="text-2xl font-black text-white mb-3">Оплата прошла успешно!</h1>
              <p className="text-zinc-400 mb-6">Спасибо за покупку. Мы свяжемся с вами в ближайшее время.</p>
              
              {paymentData && (
                <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 mb-6">
                  <p className="text-emerald-300 text-lg font-bold">
                    ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                  </p>
                </div>
              )}

              <div className="space-y-3 mb-8">
                <div className="flex items-center justify-center gap-3 text-zinc-400">
                  <Mail className="w-5 h-5 text-amber-400" />
                  <span>Проверьте вашу почту</span>
                </div>
                <div className="flex items-center justify-center gap-3 text-zinc-400">
                  <MessageCircle className="w-5 h-5 text-amber-400" />
                  <span>Мы напишем вам в Telegram/WhatsApp</span>
                </div>
              </div>

              {/* Spam notice */}
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 mb-8">
                <p className="text-amber-300 text-sm font-medium mb-1">
                  📧 Не нашли письмо?
                </p>
                <p className="text-zinc-400 text-sm">
                  Проверьте папку <span className="text-white font-semibold">«Спам»</span> или <span className="text-white font-semibold">«Промоакции»</span>
                </p>
              </div>

              <Link 
                to="/"
                className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold rounded-xl hover:shadow-lg hover:shadow-amber-500/30 hover:-translate-y-0.5 transition-all"
              >
                <span>Вернуться на главную</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </>
          )}

          {/* Error states */}
          {(status === 'error' || status === 'expired' || status === 'timeout') && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">❌</span>
              </div>
              <h1 className="text-2xl font-black text-white mb-3">
                {status === 'expired' ? 'Сессия истекла' : 'Ошибка проверки'}
              </h1>
              <p className="text-zinc-400 mb-6">
                {status === 'expired' 
                  ? 'Время на оплату истекло. Пожалуйста, попробуйте снова.'
                  : 'Не удалось проверить статус оплаты. Если деньги были списаны, свяжитесь с нами.'}
              </p>

              <Link 
                to="/#pricing"
                className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold rounded-xl hover:shadow-lg hover:shadow-amber-500/30 hover:-translate-y-0.5 transition-all"
              >
                <span>Попробовать снова</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
